import { useCallback, useEffect, useState } from "react";
import { Mail, PlayCircle, RefreshCw } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const STATUS_CLASS = { sent: "status-active", sandboxed: "status-pending", dry_run: "status-pending", queued_for_digest: "status-neutral", deferred_cap: "status-inactive", suppressed: "status-neutral", failed: "status-inactive", skipped_preview: "status-neutral", logged: "status-neutral", portal_only: "status-neutral" };
const STATUS_LABEL = { sent: "Sent", sandboxed: "Sandboxed (preview)", dry_run: "Dry run", queued_for_digest: "Held for digest", deferred_cap: "Deferred (hourly cap)", suppressed: "Suppressed (test address)", failed: "Failed", skipped_preview: "Skipped (preview)", logged: "Logged", portal_only: "Portal notice (no email)" };
const KIND_LABEL = { nudge: "scheduling reminder", digest: "digest", evaluation_report: "report upload" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString();

export default function NotificationLogPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [ranges] = useState(() => [{ value: daysAgo(1), label: "Last 24 hours" }, { value: daysAgo(7), label: "Last 7 days" }, { value: daysAgo(30), label: "Last 30 days" }]);
  const [since, setSince] = useState(ranges[0].value);
  const [recipient, setRecipient] = useState("");
  const [error, setError] = useState("");
  const [simulation, setSimulation] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData((await api.get("/notifications/log", { params: { since, recipient } })).data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [since, recipient]);

  useEffect(() => {
    load();
  }, [load]);

  async function simulate() {
    setBusy(true);
    try {
      setSimulation((await api.post("/notifications/log/simulate-daily-run")).data);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-shell" data-testid="notification-log-shell">
      <AppSidebar activeItem="Notification log" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page" data-testid="notification-log-page">
          <section className="page-intro">
            <div>
              <p className="eyebrow">OPERATIONS · EMAIL VOLUME</p>
              <h1>Notification log</h1>
              <p className="page-subtitle">Every outbound TAGForce email: what was sent, to whom and when. Scheduled jobs send one digest per recipient per run; each person receives at most {data?.hourly_cap ?? 10} emails per hour — anything more rolls into the next digest.</p>
            </div>
            <div className="verification-toolbar">
              <label>Since<select data-testid="notification-log-since" value={since} onChange={(e) => setSince(e.target.value)}>{ranges.map((r) => <option key={r.label} value={r.value}>{r.label}</option>)}</select></label>
              <label>Recipient<input data-testid="notification-log-recipient" placeholder="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} /></label>
              <button type="button" className="button button-secondary" data-testid="notification-log-refresh" onClick={load}><RefreshCw size={14} /> Refresh</button>
              {user?.role === "system_administrator" && <button type="button" className="button button-primary" data-testid="notification-log-simulate" disabled={busy} onClick={simulate}><PlayCircle size={14} /> {busy ? "Simulating…" : "Simulate 8 AM run"}</button>}
            </div>
          </section>
          {error && <p className="form-message error-message" data-testid="notification-log-error">{error}</p>}
          {simulation && (
            <section className="connecteam-panel" data-testid="notification-simulation">
              <div className="section-heading"><div><h2><Mail size={16} /> What the next 8:00 a.m. run would send</h2><p>Dry run {simulation.run_id} · {simulation.digests.recipients} digest email{simulation.digests.recipients === 1 ? "" : "s"} covering {simulation.digests.items} item{simulation.digests.items === 1 ? "" : "s"}. Nothing was emailed.</p></div></div>
              <ul className="backup-audit-list">
                {simulation.recipients.map((row) => (
                  <li key={row.recipient} data-testid={`simulation-recipient-${row.recipient}`}>
                    <strong>{row.recipient}</strong>
                    <span>{row.emails || 0} email{(row.emails || 0) === 1 ? "" : "s"} · {row.digest_items.length} item{row.digest_items.length === 1 ? "" : "s"} in the digest</span>
                    <ul className="simulation-items">{row.digest_items.map((subject, i) => <li key={i}>{subject}</li>)}</ul>
                  </li>
                ))}
                {!simulation.recipients.length && <li data-testid="simulation-empty">The run would send nothing.</li>}
              </ul>
            </section>
          )}
          {data && (
            <section className="provider-summary-grid" data-testid="notification-log-summary">
              {data.by_recipient.slice(0, 8).map((row) => (
                <button type="button" key={row.recipient} data-testid={`notification-log-recipient-${row.recipient}`} onClick={() => setRecipient(row.recipient)}>
                  <Mail size={18} /><strong>{(row.sent || 0) + (row.sandboxed || 0) + (row.dry_run || 0)}</strong><span>{row.recipient}<br /><small>{row.queued_for_digest || 0} held · {row.deferred_cap || 0} deferred</small></span>
                </button>
              ))}
              <button type="button" data-testid="notification-log-queued"><Mail size={18} /><strong>{data.queued_for_next_digest}</strong><span>waiting for the next digest</span></button>
            </section>
          )}
          <section className="requests-table-panel">
            <table className="requests-table" data-testid="notification-log-table">
              <thead><tr><th>When</th><th>Recipient</th><th>Subject</th><th>Source</th><th>Status</th></tr></thead>
              <tbody>
                {(data?.rows || []).map((row) => (
                  <tr key={row.id} data-testid={`notification-log-row-${row.id}`}>
                    <td>{fmt(row.at)}</td>
                    <td>{row.recipient}</td>
                    <td>{row.subject}{row.item_count > 1 ? <><br /><small className="pairing-meta">{row.item_count} items</small></> : null}</td>
                    <td><small className="pairing-meta">{[row.job ? `job: ${row.job}` : "", KIND_LABEL[row.kind] || row.kind || (row.job ? "" : "direct")].filter(Boolean).join(" · ")}{row.reason ? ` · ${row.reason}` : ""}</small></td>
                    <td><span className={`status-chip ${STATUS_CLASS[row.status] || "status-neutral"}`}>{STATUS_LABEL[row.status] || row.status}</span></td>
                  </tr>
                ))}
                {data && !data.rows.length && <tr><td colSpan={5} className="provider-empty-state" data-testid="notification-log-empty">No notifications in this window.</td></tr>}
              </tbody>
            </table>
          </section>
        </main>
      </div>
    </div>
  );
}
